import { styled } from 'dripsy';
import React from 'react';
import { ModalComponentProp } from 'react-native-modalfy';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import { observer, useLocalObservable } from 'mobx-react-lite';

import { ModalStackParamsType } from '../../modals/types';
import { ModalView } from './components/ModalView';
import { BaseText } from '../common/BaseText';
import { Separator } from '../common/Separator';
import { IconTouchable } from '../common/IconTouchable';
import { Button, ButtonStyleEnum } from '../common/Button';
import { ButtonsLayout } from '../layouts/ButtonsLayout';
import { ListView } from '@components/views/ListView';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { CreditItem } from '../business/CreditItem';
import type { Credits } from '@models/Tracks/CreditsModel';
import { parseApiError } from '@utils';
import { ToastService } from '@services';
import { useAsyncInit } from '@hooks';

const ModalViewContainer = styled(ModalView)({
  py: '$8',
  px: '$8',
  width: 391,
  minHeight: 400,
  maxHeight: Platform.select<string | number>({
    default: 600,
    web: '80vh',
  }),
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Title = styled(BaseText)({
  variant: 'text.xlarge',
  fontWeight: 'semi',
});

const Description = styled(BaseText)({
  variant: 'text.medium',
  color: '$text400',
});

export interface IPurchaseCreditsModalStore {
  chooseCreditOfferId: number | null;
  setChooseCreditOfferId(id: number | null): void;
  isSelectedCreditOfferId: boolean;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'PurchaseCreditsModal'
>;

export interface IPurchaseCreditsModalParams {
  onNavigateToPayment(item: Credits | undefined): void;
}

export const PurchaseCreditsModal = observer(
  ({ modal }: ModalPropsType) => {
    const onNavigateToPayment = modal.getParam('onNavigateToPayment');
    const { t } = useTranslation();
    const tracks = useTracksModel();
    const store = useLocalObservable<IPurchaseCreditsModalStore>(
      () => ({
        chooseCreditOfferId: null,

        setChooseCreditOfferId(i: number) {
          this.chooseCreditOfferId = i;
        },

        get isSelectedCreditOfferId() {
          return this.chooseCreditOfferId !== null;
        },
      }),
    );

    useAsyncInit(async () => {
      try {
        await tracks.feedback.creditOffers.fetch.run();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    });

    function closeModal() {
      modal.closeModal();
    }

    function cancelAction() {
      closeModal();
    }

    function confirmAction() {
      if (store.chooseCreditOfferId === null) {
        return;
      }
      const chooseItem = tracks.feedback.creditOfferById(
        store.chooseCreditOfferId,
      );
      closeModal();
      onNavigateToPayment(chooseItem);
    }

    return (
      <ModalViewContainer>
        <TitleContainer>
          <Title>{t('common.purchaseCredits.title')}</Title>

          <IconTouchable
            onPress={closeModal}
            name="close-light"
            size={24}
          />
        </TitleContainer>
        <Separator size="$6" />
        <Description>
          {t('common.purchaseCredits.description')}
        </Description>

        <Separator />

        <ListView
          data={tracks.feedback.creditOffers.asArray}
          renderItem={({ item }) => (
            <CreditItem item={item} store={store} />
          )}
          ItemSeparatorComponent={() => <Separator size="$5" />}
          isLoading={tracks.feedback.creditOffers.fetch.inProgress}
        />

        <Separator />

        <ButtonsLayout>
          <Button
            onPress={confirmAction}
            disabled={!store.isSelectedCreditOfferId}
          >
            {t('common.purchaseCredits.purchaseButtonTitle')}
          </Button>

          <Button
            onPress={cancelAction}
            buttonStyle={ButtonStyleEnum.Outline}
          >
            {t('common.purchaseCredits.cancel')}
          </Button>
        </ButtonsLayout>
      </ModalViewContainer>
    );
  },
);
